import axios from 'axios';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import { getConfig } from '../config';

export type SkinAnalysisMetricKey =
  | 'breakouts'
  | 'pores'
  | 'redness'
  | 'pigmentation'
  | 'lines'
  | 'sagging'
  | 'uniformness'
  | 'dark_circles'
  | 'puffiness';

export interface SkinAnalysisMetricDefinition {
  key: SkinAnalysisMetricKey;
  label: string;
  hautAliases: string[];
  iconName: string;
  description: string;
  sortOrder: number;
  topConcernEligible: boolean;
}

export type SkinAnalysisMetricDetailItemDisplayType = 'bullets' | 'tags';

export interface SkinAnalysisMetricDetailSection {
  sectionId: string;
  title: string;
  description?: string;
  askSsolQuestion?: string;
  items: string[];
  itemDisplayType: SkinAnalysisMetricDetailItemDisplayType;
}

export interface SkinAnalysisMetricRangeCopy {
  rangeStart: number;
  rangeEnd: number;
  ageRange?: string;
  description: string;
  detailSections: SkinAnalysisMetricDetailSection[];
}

export interface SkinAnalysisMetricCopyConfig {
  askSsolQuestion?: string;
  ranges: SkinAnalysisMetricRangeCopy[];
}

export interface SkinAnalysisSurveyOptionMetricMapping {
  metricKey: SkinAnalysisMetricKey;
  weight: number;
}

export interface SkinAnalysisSurveyOption {
  value: string;
  label: string;
  description?: string;
  iconImageUrl?: string;
  metricMappings?: SkinAnalysisSurveyOptionMetricMapping[];
}

export type SkinAnalysisSurveyQuestionType = 'single' | 'multi' | 'ranked_multi';

export interface SkinAnalysisSurveyQuestion {
  questionId: string;
  label: string;
  prompt: string;
  helper?: string;
  type: SkinAnalysisSurveyQuestionType;
  options: SkinAnalysisSurveyOption[];
}

export interface SkinAnalysisSurveyConfig {
  surveyKey: string;
  title: string;
  questions: SkinAnalysisSurveyQuestion[];
}

export interface SkinAnalysisRoutinePolicyStep {
  stepOrder: number;
  name: string;
  scheduleTags: Array<'AM' | 'PM'>;
  description?: string;
  category1Id: string;
  category2Id?: string;
  category3Id?: string;
  selectedCategoryId: string;
  reason?: string;
}

export interface SkinAnalysisRoutinePolicy {
  metricKey: SkinAnalysisMetricKey;
  steps: SkinAnalysisRoutinePolicyStep[];
}

export interface SkinAnalysisAdminConfigBundle {
  metrics: SkinAnalysisMetricDefinition[];
  metricCopies: Record<SkinAnalysisMetricKey, SkinAnalysisMetricCopyConfig>;
  survey: SkinAnalysisSurveyConfig;
  routinePolicies: Record<SkinAnalysisMetricKey, SkinAnalysisRoutinePolicy>;
}

const metricSeed: Array<[SkinAnalysisMetricKey, string]> = [
  ['breakouts', 'Breakouts'],
  ['pores', 'Pores'],
  ['redness', 'Redness'],
  ['pigmentation', 'Pigmentation'],
  ['lines', 'Lines'],
  ['sagging', 'Sagging'],
  ['uniformness', 'Uniformness'],
  ['dark_circles', 'Dark Circles'],
  ['puffiness', 'Under Eye Puffiness'],
];

const metrics: SkinAnalysisMetricDefinition[] = metricSeed.map(([key, label], index) => ({
  key: key as SkinAnalysisMetricKey,
  label,
  hautAliases: [key],
  iconName: 'circle',
  description: `${label} content and recommendation policy.`,
  sortOrder: index + 1,
  topConcernEligible: true,
}));

const metricRangeBuckets = Array.from({ length: 10 }, (_, index) => ({
  rangeStart: index * 10,
  rangeEnd: index === 9 ? 100 : index * 10 + 9,
}));

const rangeTone = (rangeStart: number): { description: string; howToImprove: string } => {
  if (rangeStart < 30) {
    return {
      description: 'is currently a priority concern that needs clear routine guidance.',
      howToImprove: 'Start with a gentle base routine and add one targeted active gradually.',
    };
  }
  if (rangeStart < 70) {
    return {
      description: 'has mixed signals and should be monitored with a consistent routine.',
      howToImprove: 'Maintain hydration and SPF, then target this concern with one focused step.',
    };
  }
  return {
    description: 'is currently in a stronger range and should focus on maintenance.',
    howToImprove: 'Keep the current routine steady and prevent regression with barrier care and SPF.',
  };
};

type MetricDetailExample = {
  meaning: string[];
  causes: string[];
  actions: string[];
  ingredients: string[];
};

const metricDetailExamples: Record<SkinAnalysisMetricKey, MetricDetailExample> = {
  breakouts: {
    meaning: [
      'Breakout activity is visible enough to prioritize a calming routine.',
      'Focus on keeping pores clear without stripping the skin.',
    ],
    causes: [
      'Excess oil and dead skin buildup',
      'Heavy or occlusive product layers',
      'Stress, sleep changes, or hormonal cycles',
    ],
    actions: [
      'Use a gentle cleanser morning and night',
      'Add BHA or an acne care serum slowly',
      'Keep moisturizer lightweight and non-comedogenic',
    ],
    ingredients: ['Salicylic Acid', 'Niacinamide', 'Centella', 'Tea Tree'],
  },
  pores: {
    meaning: [
      'Pores look more visible around oil-prone areas.',
      'Texture refinement and oil balance should be the main focus.',
    ],
    causes: [
      'Oil buildup around the T-zone',
      'Dehydration that makes texture more noticeable',
      'Loss of elasticity around pore walls',
    ],
    actions: [
      'Use a low-irritation exfoliating step 1-3 times weekly',
      'Keep hydration steady before stronger actives',
      'Protect with daily sunscreen to reduce texture stress',
    ],
    ingredients: ['BHA', 'Niacinamide', 'Green Tea', 'Hyaluronic Acid'],
  },
  redness: {
    meaning: [
      'Redness or visible sensitivity is a meaningful signal in this range.',
      'The routine should reduce irritation before adding strong actives.',
    ],
    causes: [
      'Compromised skin barrier',
      'Heat, weather, or friction triggers',
      'Overuse of exfoliating or brightening products',
    ],
    actions: [
      'Pause harsh exfoliants until the skin feels calm',
      'Use soothing serum and barrier cream daily',
      'Patch test new products before full-face use',
    ],
    ingredients: ['Centella', 'Panthenol', 'Ceramide', 'Madecassoside'],
  },
  pigmentation: {
    meaning: [
      'Uneven spots or discoloration need consistent brightening support.',
      'Progress usually depends on sunscreen consistency.',
    ],
    causes: [
      'UV exposure and post-blemish marks',
      'Slow turnover of pigmented cells',
      'Inflammation from previous breakouts',
    ],
    actions: [
      'Use broad-spectrum SPF every morning',
      'Add a brightening serum consistently',
      'Avoid picking blemishes to reduce post-acne marks',
    ],
    ingredients: ['Vitamin C', 'Niacinamide', 'Tranexamic Acid', 'Alpha Arbutin'],
  },
  lines: {
    meaning: [
      'Fine lines are visible enough to support hydration and renewal.',
      'The first goal is plumper, more resilient skin.',
    ],
    causes: [
      'Dehydration and low moisture retention',
      'Expression lines and UV exposure',
      'Slower collagen renewal over time',
    ],
    actions: [
      'Layer hydrating serum under moisturizer',
      'Use retinoid gradually at night if tolerated',
      'Keep SPF consistent every morning',
    ],
    ingredients: ['Retinol', 'Peptides', 'Hyaluronic Acid', 'Adenosine'],
  },
  sagging: {
    meaning: [
      'Firmness support should be included in the routine plan.',
      'The focus is elasticity, barrier comfort, and long-term prevention.',
    ],
    causes: [
      'Reduced elasticity and collagen support',
      'UV exposure over time',
      'Dryness that makes contours look less firm',
    ],
    actions: [
      'Use peptide or firming serum consistently',
      'Prioritize moisturizer that supports elasticity',
      'Avoid aggressive routines that weaken the barrier',
    ],
    ingredients: ['Peptides', 'Adenosine', 'Ceramide', 'Collagen'],
  },
  uniformness: {
    meaning: [
      'Overall tone and texture look uneven in this score range.',
      'Routine consistency matters more than adding many actives.',
    ],
    causes: [
      'Uneven hydration across the face',
      'Post-inflammatory tone changes',
      'Inconsistent exfoliation or sunscreen use',
    ],
    actions: [
      'Keep cleansing, hydration, and SPF consistent',
      'Use gentle exfoliation only as tolerated',
      'Add brightening support after barrier care is stable',
    ],
    ingredients: ['Niacinamide', 'PHA', 'Rice Extract', 'Licorice Root'],
  },
  dark_circles: {
    meaning: [
      'Under-eye darkness is a visible concern in this result.',
      'The routine should support hydration, brightness, and sun protection.',
    ],
    causes: ['Thin under-eye skin and shadowing', 'Sleep changes or dehydration', 'Pigmentation around the eye area'],
    actions: [
      'Use a hydrating eye cream or lightweight serum',
      'Apply sunscreen carefully around the orbital area',
      'Avoid rubbing the eye area when removing makeup or sunscreen',
    ],
    ingredients: ['Caffeine', 'Niacinamide', 'Peptides', 'Vitamin C'],
  },
  puffiness: {
    meaning: [
      'Under-eye puffiness is noticeable enough to guide routine choices.',
      'Cooling, hydration balance, and gentle application are the main focus.',
    ],
    causes: [
      'Fluid retention around the under-eye area',
      'Sleep position or salty meals',
      'Irritation from rubbing or heavy eye products',
    ],
    actions: [
      'Use lightweight eye care and gentle tapping application',
      'Try cooling tools when puffiness is visible',
      'Avoid heavy creams close to the lash line',
    ],
    ingredients: ['Caffeine', 'Green Tea', 'Panthenol', 'Peptides'],
  },
};

const detailSections = (label: string, howToImprove: string): SkinAnalysisMetricDetailSection[] => [
  {
    sectionId: 'what-this-means',
    title: 'What this means',
    description: `Short explanation shown at the top of the ${label.toLowerCase()} detail card.`,
    askSsolQuestion: `What does my ${label.toLowerCase()} score mean?`,
    items: metricDetailExamples[labelToMetricKey(label)].meaning,
    itemDisplayType: 'bullets',
  },
  {
    sectionId: 'why-it-happens',
    title: 'Why it happens',
    description: `Likely drivers behind the current ${label.toLowerCase()} result.`,
    askSsolQuestion: `Why is my ${label.toLowerCase()} result showing this way?`,
    items: metricDetailExamples[labelToMetricKey(label)].causes,
    itemDisplayType: 'bullets',
  },
  {
    sectionId: 'how-to-improve',
    title: 'How to improve',
    description: howToImprove,
    askSsolQuestion: `How should I improve my ${label.toLowerCase()} result first?`,
    items: metricDetailExamples[labelToMetricKey(label)].actions,
    itemDisplayType: 'bullets',
  },
  {
    sectionId: 'key-ingredients',
    title: 'Key ingredients',
    description: 'Ingredient tags used as copy hints for the report detail UI.',
    askSsolQuestion: `Which ingredients should I look for based on my ${label.toLowerCase()} result?`,
    items: metricDetailExamples[labelToMetricKey(label)].ingredients,
    itemDisplayType: 'tags',
  },
];

const labelToMetricKey = (label: string): SkinAnalysisMetricKey =>
  metrics.find((metric) => metric.label === label)?.key ?? 'breakouts';

const metricCopies = Object.fromEntries(
  metrics.map((metric) => [
    metric.key,
    {
      askSsolQuestion: `Can you explain my ${metric.label.toLowerCase()} result and what I should focus on first?`,
      ranges: metricRangeBuckets.map(({ rangeStart, rangeEnd }) => {
        const tone = rangeTone(rangeStart);
        return {
          rangeStart,
          rangeEnd,
          description: `This ${rangeStart}-${rangeEnd} range should explain why ${metric.label.toLowerCase()} ${tone.description}`,
          detailSections: detailSections(metric.label, tone.howToImprove),
        };
      }),
    },
  ]),
) as Record<SkinAnalysisMetricKey, SkinAnalysisMetricCopyConfig>;

const routinePolicies = Object.fromEntries(
  metrics.map((metric) => [
    metric.key,
    {
      metricKey: metric.key,
      steps: [
        {
          stepOrder: 1,
          name: 'Gentle cleanser',
          scheduleTags: ['AM', 'PM'],
          description: 'Start with a low-irritation cleanse before targeted treatment steps.',
          category1Id: 'skincare',
          category2Id: 'cleanser',
          selectedCategoryId: 'cleanser',
          reason: 'Creates a low-irritation base routine.',
        },
        {
          stepOrder: 2,
          name: `${metric.label} care serum`,
          scheduleTags: ['AM'],
          description: `Use a targeted day step for ${metric.label.toLowerCase()} without overloading the routine.`,
          category1Id: 'skincare',
          category2Id: 'serum',
          selectedCategoryId: 'serum',
          reason: `Supports ${metric.label.toLowerCase()} when this metric needs attention.`,
        },
        {
          stepOrder: 3,
          name: `${metric.label} night treatment`,
          scheduleTags: ['PM'],
          description: `Use the night treatment window to support ${metric.label.toLowerCase()} while skin rests.`,
          category1Id: 'skincare',
          category2Id: 'treatment',
          selectedCategoryId: 'treatment',
          reason: `Supports ${metric.label.toLowerCase()} while skin rests.`,
        },
      ],
    },
  ]),
) as Record<SkinAnalysisMetricKey, SkinAnalysisRoutinePolicy>;

export const SKIN_ANALYSIS_ADMIN_CONFIG_DEFAULTS: SkinAnalysisAdminConfigBundle = {
  metrics,
  metricCopies,
  survey: {
    surveyKey: 'skin-analysis-p1',
    title: 'Skin Analysis P1 Survey',
    questions: [
      {
        questionId: 'skin_type_self_reported',
        label: 'Self-reported skin type',
        prompt: 'What would you say your skin type is?',
        helper: 'This helps adjust final routine comfort.',
        type: 'single',
        options: [
          { value: 'Oily', label: 'Oily', description: 'Shiny through the day.' },
          { value: 'Dry', label: 'Dry', description: 'Tight or flaky.' },
          { value: 'Combination', label: 'Combination', description: 'Oily T-zone and drier cheeks.' },
          { value: 'Sensitive', label: 'Sensitive', description: 'Reacts easily to products.' },
        ],
      },
      {
        questionId: 'skin_concerns',
        label: 'Primary skin concerns',
        prompt: 'Which concerns do you want to focus on?',
        helper: 'This can be configured as a multi-select question.',
        type: 'multi',
        options: [
          {
            value: 'Breakouts',
            label: 'Breakouts',
            description: 'Blemishes or acne-prone moments.',
            iconImageUrl: '',
            metricMappings: [{ metricKey: 'breakouts', weight: 1 }],
          },
          {
            value: 'Pores',
            label: 'Pores',
            description: 'Visible pores or texture around cheeks and nose.',
            iconImageUrl: '',
            metricMappings: [{ metricKey: 'pores', weight: 1 }],
          },
          {
            value: 'Redness',
            label: 'Redness',
            description: 'Visible redness or sensitivity.',
            iconImageUrl: '',
            metricMappings: [{ metricKey: 'redness', weight: 1 }],
          },
        ],
      },
    ],
  },
  routinePolicies,
};

type LegacySkinAnalysisRoutinePolicy = Partial<SkinAnalysisRoutinePolicy> & {
  routines?: Record<'AM' | 'PM', SkinAnalysisRoutinePolicyStep[]>;
  steps?: SkinAnalysisRoutinePolicyStep[];
};

const normalizeScheduleTags = (
  scheduleTags: SkinAnalysisRoutinePolicyStep['scheduleTags'] | undefined,
): Array<'AM' | 'PM'> => {
  const normalized = (scheduleTags ?? []).filter((tag): tag is 'AM' | 'PM' => tag === 'AM' || tag === 'PM');
  return normalized.length > 0 ? Array.from(new Set(normalized)) : ['AM'];
};

const normalizeRoutineStepOrder = (steps: SkinAnalysisRoutinePolicyStep[]): SkinAnalysisRoutinePolicyStep[] =>
  steps.map((step, index) => ({
    ...step,
    stepOrder: index + 1,
    scheduleTags: normalizeScheduleTags(step.scheduleTags),
    selectedCategoryId: step.selectedCategoryId || step.category3Id || step.category2Id || step.category1Id,
  }));

const normalizeRoutinePolicy = (
  metricKey: SkinAnalysisMetricKey,
  policy: LegacySkinAnalysisRoutinePolicy,
): SkinAnalysisRoutinePolicy => {
  if (Array.isArray(policy.steps)) {
    return {
      metricKey,
      steps: normalizeRoutineStepOrder(policy.steps),
    };
  }

  const legacySteps = [
    ...(policy.routines?.AM ?? []).map((step) => ({ ...step, scheduleTags: ['AM'] as Array<'AM' | 'PM'> })),
    ...(policy.routines?.PM ?? []).map((step) => ({ ...step, scheduleTags: ['PM'] as Array<'AM' | 'PM'> })),
  ];

  return {
    metricKey,
    steps: normalizeRoutineStepOrder(legacySteps),
  };
};

const normalizeBundle = (bundle: SkinAnalysisAdminConfigBundle): SkinAnalysisAdminConfigBundle => ({
  ...bundle,
  routinePolicies: Object.fromEntries(
    Object.entries(bundle.routinePolicies).map(([metricKey, policy]) => [
      metricKey,
      normalizeRoutinePolicy(metricKey as SkinAnalysisMetricKey, policy as LegacySkinAnalysisRoutinePolicy),
    ]),
  ) as Record<SkinAnalysisMetricKey, SkinAnalysisRoutinePolicy>,
});

export class SkinAnalysisAdminConfigClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  public constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.SKIN_ANALYSIS_CONFIG);
    this.headers = { 'X-API-KEY': getConfig().apiKey };
  }

  public async getConfig(): Promise<SkinAnalysisAdminConfigBundle> {
    const response = await axios.get<SkinAnalysisAdminConfigBundle>(`${this.baseUrl}/skinAnalysis/admin/config`, {
      headers: this.headers,
    });
    return normalizeBundle(response.data);
  }

  public async getDefaults(): Promise<SkinAnalysisAdminConfigBundle> {
    const response = await axios.get<SkinAnalysisAdminConfigBundle>(
      `${this.baseUrl}/skinAnalysis/admin/config/defaults`,
      {
        headers: this.headers,
      },
    );
    return normalizeBundle(response.data);
  }

  public async saveConfig(request: SkinAnalysisAdminConfigBundle): Promise<SkinAnalysisAdminConfigBundle> {
    const response = await axios.put<SkinAnalysisAdminConfigBundle>(
      `${this.baseUrl}/skinAnalysis/admin/config`,
      request,
      {
        headers: this.headers,
      },
    );
    return normalizeBundle(response.data);
  }
}
